import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, CheckCircle, Shield, CreditCard, Loader2, AlertCircle } from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const PaymentProduct = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const features = [
    "Module 1 : Introduction au e-commerce digital",
    "Module 2 : Création de boutique en ligne",
    "Module 3 : Stratégies de marketing digital",
    "Module 4 : Gestion des ventes et logistique",
    "Module 5 : Optimisation et croissance",
    "Module 6 : Service client et fidélisation",
    "Mises à jour régulières du contenu",
    "Accès immédiat après paiement"
  ];

  const handlePurchase = async () => {
    if (!isAuthenticated) {
      navigate('/connexion?redirect=/payment/product');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Étape 1: Créer un devis signé (Site A → Site B)
      const quoteRes = await api.post('/payment/quotes/create', {
        user_id: user.id,
        user_email: user.email,
        product_id: 'book_formation_ecommerce'
      });

      const { quote_id, signature } = quoteRes.data;

      // Étape 2: Créer la session Stripe Checkout (Site B)
      const checkoutRes = await api.post('/payment/checkout/create', {
        quote_id,
        signature,
        origin_url: window.location.origin
      });

      // Étape 3: Rediriger vers Stripe Checkout
      if (checkoutRes.data.checkout_url) {
        window.location.href = checkoutRes.data.checkout_url;
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erreur lors de la création du paiement';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="py-12">
      <div className="max-w-4xl mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Product Info */}
          <div>
            <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl p-8 text-white mb-6">
              <div className="flex items-center gap-3 mb-4">
                <BookOpen className="h-8 w-8" />
                <h1 className="text-2xl font-bold">Book Formation E-Commerce</h1>
              </div>
              <p className="text-blue-100 mb-6">
                Formation complète en e-commerce digital. Développez vos compétences et lancez votre activité en ligne avec confiance.
              </p>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                <div className="text-3xl font-bold mb-1">59€</div>
                <p className="text-sm text-blue-200">Paiement unique — Accès immédiat</p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Contenu de la formation</h2>
              <div className="space-y-3">
                {features.map((feature, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-600">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Purchase Section */}
          <div>
            <div className="bg-white rounded-xl p-6 border border-gray-200 sticky top-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Récapitulatif</h2>
              
              <div className="space-y-4 mb-6">
                <div className="flex justify-between items-center pb-4 border-b border-gray-100">
                  <span className="text-gray-600">Book Formation E-Commerce</span>
                  <span className="font-semibold text-gray-900">59,00€</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-gray-900">Total</span>
                  <span className="text-2xl font-bold text-blue-600">59,00€</span>
                </div>
              </div>

              {error && (
                <div className="flex items-center gap-2 bg-red-50 text-red-600 text-sm p-3 rounded-lg mb-4">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <button
                onClick={handlePurchase}
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white font-semibold py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Redirection vers le paiement...
                  </>
                ) : (
                  <>
                    <CreditCard className="h-5 w-5" />
                    Payer 59,00€
                  </>
                )}
              </button>

              <div className="mt-4 space-y-3">
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  <Shield className="h-3 w-3 text-green-500" />
                  <span>Paiement sécurisé via Stripe</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  <CreditCard className="h-3 w-3" />
                  <span>Carte bancaire acceptée</span>
                </div>
                <p className="text-xs text-gray-400">
                  {"En cliquant sur « Payer », vous acceptez nos "}
                  <a href="/payment/cgv" className="underline hover:text-blue-600">{"conditions générales de vente"}</a>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentProduct;
